/**
 * The vocabulary a verdict's badge speaks, wherever one is drawn.
 *
 * The fold-out's pill and the analytics sheet's chip answer one question about
 * one verdict, so they resolve it here rather than each keeping a table of its
 * own: two tables are two vocabularies, free to disagree about a verdict while
 * each claims to be stating it.
 *
 * A module of its own rather than an export from either host because
 * `react-refresh/only-export-components` refuses a component file that also
 * exports a function, and the repo lints at `--max-warnings 0`.
 */

/** Pill styling for a given outcome value (incl. the synthetic "inconclusive"),
 * with a matching border, and the hover tint a clickable badge adds to it. */
export function outcomeBadgeStyle(value: string): {
  label: string;
  classes: string;
  hoverClasses: string;
} {
  switch (value) {
    case "pass":
      return {
        label: "Pass",
        classes: "border-emerald-200 bg-emerald-50 text-emerald-700",
        hoverClasses: "hover:bg-emerald-100",
      };
    case "fail":
      return {
        label: "Fail",
        classes: "border-red-200 bg-red-50 text-red-700",
        hoverClasses: "hover:bg-red-100",
      };
    case "not_applicable":
      return {
        label: "N/A",
        classes: "border-stone-200 bg-stone-100 text-stone-600",
        hoverClasses: "hover:bg-stone-200",
      };
    default:
      return {
        label: "Inconclusive",
        classes: "border-amber-200 bg-amber-50 text-amber-700",
        hoverClasses: "hover:bg-amber-100",
      };
  }
}

/**
 * The pill's value for a verdict — its outcome, or the synthetic
 * "inconclusive" where no answer has been reached.
 *
 * A conclusive verdict carrying no outcome reads as a failure, the same reading
 * the analytics translator gives one: a judgment that concluded without an
 * answer is not a pass.
 */
export function outcomeBadgeValue(verdict: {
  outcome: "pass" | "fail" | "not_applicable" | null;
  isConclusive: boolean;
}): string {
  return verdict.isConclusive ? (verdict.outcome ?? "fail") : "inconclusive";
}
